"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowRight, Clock } from "lucide-react";
import type { Post } from "@/data/posts";

export type { Post };

/**
 * Blog index list with client-side cluster filter.
 * - "All" shows the featured-card-on-left + editorial-grid pattern.
 * - A specific cluster shows all matching posts in a single grid (the
 *   "featured" card only makes sense in the unfiltered view).
 */
export function BlogList({
  posts,
  clusters,
}: {
  posts: Post[];
  clusters: string[];
}) {
  const [active, setActive] = useState<string>("All");

  const filtered =
    active === "All" ? posts : posts.filter((p) => p.cluster === active);

  const showFeatured = active === "All" && filtered.length > 0;
  const [featured, ...rest] = showFeatured ? filtered : [];
  const gridPosts = showFeatured ? rest : filtered;

  return (
    <>
      {/* Cluster filter strip */}
      <section className="bg-bg border-b border-border">
        <div className="site-container py-6 flex flex-wrap items-center gap-3">
          {clusters.map((c) => {
            const isActive = active === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                aria-pressed={isActive}
                className={[
                  "rounded-pill border px-4 py-1.5 text-small transition-all duration-150",
                  isActive
                    ? "border-accent bg-accent text-accent-ink font-semibold"
                    : "border-border-strong text-text-muted hover:border-accent hover:text-accent",
                ].join(" ")}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* Featured — only when not filtered */}
      {showFeatured && featured && (
        <section className="bg-bg-soft border-b border-border">
          <div className="site-container py-12 lg:py-16">
            <Link
              href={`/blog/${featured.slug}`}
              className="group block rounded-lg border border-border bg-bg overflow-hidden transition-all duration-300 hover:border-accent/40 hover:-translate-y-0.5"
            >
              <div className="grid gap-0 lg:grid-cols-2">
                <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[360px] bg-gradient-to-br from-bg-elevated via-bg-soft to-bg overflow-hidden">
                  {featured.coverImage ? (
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${featured.coverImage})` }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                      <span
                        className="font-extrabold uppercase tracking-tight text-text/20"
                        style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}
                      >
                        {featured.cluster.split(" ")[0]}
                      </span>
                    </div>
                  )}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.65) 100%)",
                    }}
                  />
                  <div aria-hidden="true" className="absolute left-6 top-6 h-px w-12 bg-accent" />
                  <div aria-hidden="true" className="absolute left-6 top-6 w-px h-12 bg-accent" />
                  <div aria-hidden="true" className="absolute bottom-6 right-6 size-2 rounded-full bg-accent" />
                  <span className="absolute left-6 bottom-6 inline-flex items-center gap-2 rounded-pill bg-accent px-3 py-1 text-[0.625rem] font-bold uppercase tracking-wider text-accent-ink">
                    Featured
                  </span>
                </div>
                <div className="p-7 lg:p-10 flex flex-col justify-center">
                  <p className="text-small uppercase tracking-wider text-accent">
                    {featured.cluster}
                  </p>
                  <h2 className="mt-3 text-h2">{featured.title}</h2>
                  <p className="mt-4 text-lead text-text-muted">{featured.excerpt}</p>
                  <div className="mt-6">
                    <Byline author={featured.author} readMinutes={featured.readMinutes} />
                  </div>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-small font-semibold text-accent">
                    Read article
                    <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1.5" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Post grid */}
      <section className="bg-bg border-b border-border">
        <div className="site-container py-16 lg:py-20">
          {gridPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-h3 text-text-muted">No articles in this cluster yet.</p>
              <button
                type="button"
                onClick={() => setActive("All")}
                className="mt-4 inline-flex items-center gap-1.5 text-small font-semibold text-accent link-anim"
              >
                See all articles
                <ArrowRight className="size-4" />
              </button>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {gridPosts.map((post) => (
                <Link
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                  className="group rounded-md border border-border bg-bg-soft overflow-hidden transition-all duration-300 hover:border-accent/40 hover:-translate-y-0.5"
                >
                  <div className="relative aspect-video bg-gradient-to-br from-bg-elevated via-bg-soft to-bg overflow-hidden">
                    {post.coverImage ? (
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                        style={{ backgroundImage: `url(${post.coverImage})` }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-h2 font-extrabold uppercase tracking-tight text-text/20">
                          {post.cluster.split(" ")[0]}
                        </span>
                      </div>
                    )}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)",
                      }}
                    />
                    <div aria-hidden="true" className="absolute left-5 top-5 h-px w-10 bg-accent" />
                    <div aria-hidden="true" className="absolute left-5 top-5 w-px h-10 bg-accent" />
                  </div>
                  <div className="p-6">
                    <p className="text-small uppercase tracking-wider text-accent">{post.cluster}</p>
                    <h3 className="mt-3 text-h3">{post.title}</h3>
                    <p className="mt-3 text-body text-text-muted">{post.excerpt}</p>
                    <div className="mt-5 pt-5 border-t border-border">
                      <Byline author={post.author} readMinutes={post.readMinutes} compact />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <p className="mt-10 text-small text-text-muted">
            Showing {gridPosts.length + (showFeatured ? 1 : 0)} of {posts.length} articles
            {active !== "All" && (
              <>
                {" "}filtered by <span className="text-accent">{active}</span>
              </>
            )}
            .
          </p>
        </div>
      </section>
    </>
  );
}

function Byline({
  author,
  readMinutes,
  compact = false,
}: {
  author: Post["author"];
  readMinutes: number;
  compact?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="inline-flex size-8 items-center justify-center rounded-full bg-bg-elevated border border-border-strong text-[0.6875rem] font-bold text-accent"
      >
        {author.initials}
      </span>
      <span className="text-small text-text">{author.name}</span>
      <span className="text-small text-text-muted">·</span>
      <span className="inline-flex items-center gap-1 text-small text-text-muted">
        <Clock className={compact ? "size-3.5" : "size-4"} />
        {readMinutes} min read
      </span>
    </div>
  );
}
